/**
 * Estado de cada cuota: saldos por componente, mora diaria y estado derivado.
 */
import { keyedEnum } from '../catalogs/enum.js';
import { daysBetween, maxIsoDate } from '../calendar/iso-date.js';
import { applyRate, BASIS_POINTS_PER_UNIT, divideRounded, nonNegative } from '../money/money.js';
/** Base de días del año para el interés moratorio (año calendario, no comercial de 360). */
export const DAYS_PER_YEAR_FOR_MORA = 365;
/** Saldo corriente (capital, interés e IVA) aún no pagado de la cuota. */
export function regularOutstanding(inst) {
    return {
        principal: nonNegative(inst.principalDue - inst.principalPaid),
        interest: nonNegative(inst.interestDue - inst.interestPaid),
        tax: nonNegative(inst.taxDue - inst.taxPaid),
    };
}
/** Interés moratorio sobre `principal` por `days` días: P × tasa × días / 365. */
export function moratoryInterest(principal, annualRate, days) {
    if (days <= 0 || principal <= 0)
        return 0;
    return divideRounded(principal * annualRate * days, BASIS_POINTS_PER_UNIT * DAYS_PER_YEAR_FOR_MORA);
}
/**
 * Saldo de la cuota a la fecha `asOf`, incluida la mora devengada desde el
 * último pago si se superaron los días de gracia.
 */
export function installmentBalance(inst, asOf, policy) {
    const regular = regularOutstanding(inst);
    const hasRegularDebt = regular.principal + regular.interest + regular.tax > 0;
    const daysLate = hasRegularDebt ? Math.max(0, daysBetween(inst.dueDate, asOf)) : 0;
    const isBeyondGrace = daysLate > policy.graceDays;
    let accrued = inst.lateInterestAccrued;
    if (isBeyondGrace) {
        const accrualStart = inst.lateAccruedThrough ? maxIsoDate(inst.lateAccruedThrough, inst.dueDate) : inst.dueDate;
        accrued += moratoryInterest(regular.principal, policy.annualRate, daysBetween(accrualStart, asOf));
    }
    const feeCharged = Math.max(inst.lateFeeCharged, isBeyondGrace ? policy.fixedFee : 0);
    const lateInterest = nonNegative(accrued - inst.lateInterestPaid);
    const lateTax = nonNegative(applyRate(accrued, policy.taxRate) - inst.lateTaxPaid);
    const lateFee = nonNegative(feeCharged - inst.lateFeePaid);
    return {
        ...regular,
        lateInterest,
        lateTax,
        lateFee,
        total: (regular.principal + regular.interest + regular.tax + lateInterest + lateTax + lateFee),
        daysLate,
        lateInterestAccruedAsOf: accrued,
        lateFeeChargedAsOf: feeCharged,
    };
}
/** Estados de una cuota. */
export const INSTALLMENT_STATUSES = ['pending', 'partial', 'overdue', 'paid', 'cancelled'];
/** Catálogo con clave de estados de cuota: `INSTALLMENT_STATUS.paid`. */
export const INSTALLMENT_STATUS = keyedEnum(INSTALLMENT_STATUSES);
/** Etiquetas en español de cada estado de cuota. */
export const INSTALLMENT_STATUS_LABELS = {
    pending: 'Pendiente',
    partial: 'Abonada',
    overdue: 'Vencida',
    paid: 'Pagada',
    cancelled: 'Anulada',
};
/** Estado derivado de una cuota a una fecha (el estado guardado es una caché de esto). */
export function installmentStatus(inst, asOf, policy) {
    const balance = installmentBalance(inst, asOf, policy);
    if (balance.total === 0)
        return INSTALLMENT_STATUS.paid;
    if (inst.dueDate < asOf)
        return INSTALLMENT_STATUS.overdue;
    const paidSomething = inst.principalPaid + inst.interestPaid + inst.taxPaid > 0;
    return paidSomething ? INSTALLMENT_STATUS.partial : INSTALLMENT_STATUS.pending;
}
/** Indica si aún no se ha pagado nada de la cuota. */
export function isUntouched(inst) {
    return (inst.principalPaid + inst.interestPaid + inst.taxPaid + inst.lateInterestPaid + inst.lateTaxPaid + inst.lateFeePaid === 0);
}
//# sourceMappingURL=installment.js.map